import {useContext, useEffect, useState} from "react";
import {Link, useNavigate, useParams} from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import ImageWithLoader from "../../components/image-loader/image-with-loader.component";
import {
  deleteBlogPost,
  getBlogPostBySlug,
} from "../../utils/firebase/firebase.utils";
import {formatDate, isAdmin, readTime} from "../../utils/blog.constants";
import {UserContext} from "../../context/user.context";
import "./blog-post.styles.scss";

const BlogPost = () => {
  const {slug} = useParams();
  const navigate = useNavigate();
  const {currentUser} = useContext(UserContext);
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const admin = isAdmin(currentUser);

  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      try {
        const data = await getBlogPostBySlug(slug);
        setPost(data);
      } catch (error) {
        console.error("Failed to load post", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  const handleDelete = async () => {
    if (!window.confirm("Delete this post? This cannot be undone.")) return;
    try {
      await deleteBlogPost(post.id);
      navigate("/blog");
    } catch (error) {
      console.error("Failed to delete post", error);
      alert("Could not delete the post. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="blog-post">
        <p className="blog-post-status">Loading…</p>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="blog-post">
        <div className="blog-post-status">
          <p>Post not found.</p>
          <Link to="/blog" className="blog-back-link">
            ← Back to all posts
          </Link>
        </div>
      </div>
    );
  }

  const {title, content, coverImage, tags = [], createdAt, authorName} = post;

  return (
    <article className="blog-post">
      <div className="blog-post-container">
        <Link to="/blog" className="blog-back-link">
          ← All posts
        </Link>

        {tags.length > 0 && (
          <div className="blog-post-tags">
            {tags.map((tag) => (
              <span key={tag} className="blog-tag">
                {tag}
              </span>
            ))}
          </div>
        )}

        <h1 className="blog-post-title">{title}</h1>

        <div className="blog-post-meta">
          <span>{authorName}</span>
          <span className="dot">·</span>
          <span>{formatDate(createdAt)}</span>
          <span className="dot">·</span>
          <span>{readTime(content)} min read</span>
        </div>

        {admin && (
          <div className="blog-post-admin">
            <Link to={`/blog/${slug}/edit`} className="blog-admin-btn">
              Edit
            </Link>
            <button
              type="button"
              onClick={handleDelete}
              className="blog-admin-btn danger"
            >
              Delete
            </button>
          </div>
        )}

        {coverImage && (
          <div className="blog-post-cover">
            <ImageWithLoader
              src={coverImage}
              alt={title}
              wrapperClassName="img-loader--cover"
            />
          </div>
        )}

        <div className="blog-post-content markdown-body">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={{
              img: ({node, ...props}) => <ImageWithLoader {...props} />,
            }}
          >
            {content}
          </ReactMarkdown>
        </div>
      </div>
    </article>
  );
};

export default BlogPost;
